// Documents module types and totals utility

export type DocumentSource = 'quotedock' | 'jarvis';
export type DocumentKind = 'Estimate' | 'Quotation' | 'Invoice' | 'CISCertificate';
/** Kinds that have PDF settings and a document template: document kinds plus Invoicing-page account invoices. */
export type TemplateKind = DocumentKind | 'AccountInvoice';
export type DocumentStatus = 'draft' | 'sent' | 'accepted' | 'rejected' | 'invoiced' | 'paid' | 'cash' | 'void';
export type LineKind = 'material' | 'equipment' | 'labour' | 'other';
export type AdditionalType = 'glass' | 'window' | 'door' | 'window_door' | 'window_glass' | 'door_glass' | 'other';

export const ADDITIONAL_TYPE_OPTIONS: Array<{ value: AdditionalType; label: string }> = [
  { value: 'glass', label: 'Glass' },
  { value: 'window', label: 'Window(s)' },
  { value: 'door', label: 'Door(s)' },
  { value: 'window_door', label: 'Window(s) & Door(s)' },
  { value: 'window_glass', label: 'Window(s) & Glass' },
  { value: 'door_glass', label: 'Door(s) & Glass' },
  { value: 'other', label: 'Other' },
];

export function getAdditionalTypeLabel(value: AdditionalType | null | undefined): string {
  return ADDITIONAL_TYPE_OPTIONS.find((option) => option.value === value)?.label ?? '—';
}

export interface Organisation {
  id: string;
  name: string;
  code: string;
}

export interface DocumentFeesConfig {
  adminPct: number; // 0..1
  adminCap: number; // currency
  contingencyPct: number; // 0..1
  profitPct: number; // 0..1
  developerPct?: number | null; // 0..1 optional
}

export interface DocumentTotalsBreakdown {
  materialsTotal: number;
  equipmentTotal: number;
  labourTotal: number;
  otherTotal: number; // additional costs
  baseCost: number;
  adminFee: number;
  contingency: number;
  developer: number;
  preProfit: number;
  profit: number;
  grandTotal: number;
}

export interface DocumentLineInput {
  lineKind: LineKind;
  additionalType?: AdditionalType | null;
  materialId?: string | null;
  equipmentId?: string | null;
  labourRateId?: string | null;
  name: string;
  description?: string | null;
  qty: number;
  unit?: string | null;
  unitPrice: number;
  vatInclusive?: boolean; // per-line flag
  lineTotal?: number; // if omitted, computed as qty*unitPrice
  sortOrder?: number | null;
  /**
   * Flexible JSON metadata for this line.
   * Used by evolving features (e.g., Quote Mode Visual Designer) without schema churn.
   */
  metaJson?: any;
  // Labour duration display fields (for estimates/quotes only)
  labourDays?: number | null;
  labourHours?: number | null;
}

export interface DocumentLine extends DocumentLineInput {
  id: string;
  documentId: string;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentInput {
  source?: DocumentSource; // default jarvis
  kind: DocumentKind;
  lifecycleRef: string; // suggested/minted
  organisationId: string;
  isOriginal?: boolean; // marks the origin record for a lifecycle
  projectId?: string | null;
  customerId?: string | null;
  /** Directory account billed instead of a customer (non-customer invoicing). */
  accountId?: string | null;
  /** Null = standard document VAT. Set on account invoices that do not charge VAT normally. */
  vatTreatment?: 'standard' | 'reverse_charge' | 'exempt' | 'outside_scope' | null;
  quotedockCustomerId?: number | null;
  customerFirstName?: string | null;
  customerLastName?: string | null;
  customerTitle?: string | null;
  title: string;
  description?: string | null;
  notes?: string | null;
  status?: DocumentStatus; // default draft
  currency?: string; // default GBP
  depositAmount?: number; // default 0
  depositPercentage?: number | null; // 0..1 for Project Events integration
  hasDeposit?: boolean; // for Project Events integration
  projectDays?: number | null; // Display field for project duration in days
  projectHours?: number | null; // Display field for project duration in hours
  vatEnabled?: boolean;
  vatPct?: number;
  inputVatSelected?: boolean;
  issuedAt?: string | null; // User-defined document issue date
  paymentTermsInDays?: number | null; // Invoice only. 0 = due on receipt.
  // Optional payment structure metadata (does NOT affect totals)
  paymentStructureApplicable?: boolean;
  paymentStructurePattern?: string | null; // e.g. "20/20/30/30", "25/50/25", "30/30/40", "30/40/30", "40/30/30", "40/40/20", "50/25/25", "50/40/10"
  invoiceRole?: 'deposit' | 'works' | 'variation' | 'credit' | null;
  frozenAt?: string | null;
  sourceSurveyEvent?: string | null;
  invoiceRef?: string | null;
  fees: DocumentFeesConfig;
}

export interface Document extends Required<Omit<DocumentInput, 'fees' | 'source' | 'status' | 'currency' | 'accountId' | 'vatTreatment' | 'depositAmount' | 'inputVatSelected' | 'depositPercentage' | 'hasDeposit' | 'issuedAt' | 'paymentTermsInDays' | 'projectDays' | 'projectHours' | 'paymentStructureApplicable' | 'paymentStructurePattern' | 'invoiceRole' | 'frozenAt' | 'sourceSurveyEvent' | 'invoiceRef'>> {
  id: string;
  source: DocumentSource;
  status: DocumentStatus;
  currency: string;
  isOriginal: boolean;
  accountId?: string | null;
  vatTreatment?: DocumentInput['vatTreatment'];
  /**
   * For versioned documents (Quotation / Estimate), this points at the currently active version.
   * In UI lists we should prefer the active version header title over the base document title.
   */
  activeVersionId?: string | null;
  depositAmount: number;
  depositPercentage: number | null;
  hasDeposit: boolean;
  projectDays: number | null;
  projectHours: number | null;
  fees: DocumentFeesConfig;
  inputVatSelected?: boolean;
  issuedAt: string | null; // User-defined document issue date
  paymentTermsInDays: number | null;
  notes: string | null;
  customerFirstName: string | null;
  customerLastName: string | null;
  createdBy?: string | null;
  updatedBy?: string | null;
  createdAt: string;
  updatedAt: string;
  statusChangedAt?: string | null;
  draftAt?: string | null;
  sentAt?: string | null;
  acceptedAt?: string | null;
  rejectedAt?: string | null;
  invoicedAt?: string | null;
  paidAt?: string | null;
  voidedAt?: string | null;
  // Database-stored totals (source of truth for integrations and summary views)
  netTotal?: number;
  vatAmount?: number;
  grossTotal?: number;
  // Optional payment structure snapshot
  paymentStructureApplicable?: boolean;
  paymentStructurePattern?: string | null;
  invoiceRole?: 'deposit' | 'works' | 'variation' | 'credit' | null;
  frozenAt?: string | null;
  sourceSurveyEvent?: string | null;
  invoiceRef?: string | null;
}

export interface DocumentWithLines extends Document {
  lines: DocumentLine[];
  totals: DocumentTotalsBreakdown;
}

function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function computeTotals(
  lines: Array<Pick<DocumentLineInput, 'lineKind' | 'qty' | 'unitPrice' | 'lineTotal' | 'vatInclusive'>>,
  fees: DocumentFeesConfig,
  vat?: { enabled: boolean; pct: number }
): DocumentTotalsBreakdown {
  const applyVat = vat?.enabled !== false; // default true
  const vatPct = vat?.pct ?? 0.2;

  // Calculate net amounts (excluding VAT) for each line item
  const getNetAmount = (line: Pick<DocumentLineInput, 'qty' | 'unitPrice' | 'lineTotal' | 'vatInclusive'>): number => {
    const grossAmount = line.lineTotal != null ? line.lineTotal : (line.qty || 0) * (line.unitPrice || 0);
    
    // If VAT inclusive and VAT is enabled, remove VAT from the gross amount
    if (line.vatInclusive && applyVat) {
      return round2(grossAmount / (1 + vatPct));
    }
    
    // Otherwise, treat as net amount (no VAT to remove)
    return round2(grossAmount);
  };

  const sumBy = (kinds: LineKind[]): number =>
    round2(
      lines
        .filter(l => kinds.includes(l.lineKind))
        .reduce((acc, l) => acc + getNetAmount(l), 0)
    );

  const materialsTotal = sumBy(['material']);
  const equipmentTotal = sumBy(['equipment']);
  const labourTotal = sumBy(['labour']);
  const otherTotal = sumBy(['other']);

  const baseCost = round2(materialsTotal + equipmentTotal + labourTotal + otherTotal);

  const adminFeeUncapped = round2(baseCost * (fees.adminPct ?? 0));
  const adminFee = round2(Math.min(adminFeeUncapped, fees.adminCap ?? 0));

  const contingency = round2(baseCost * (fees.contingencyPct ?? 0));
  const developer = 0; // developer fee removed

  // Profit margin is calculated on base cost ONLY
  const preProfit = round2(baseCost + adminFee + contingency + developer);
  const profit = round2(baseCost * (fees.profitPct ?? 0));
  const subtotal = round2(preProfit + profit);
  
  // Now add VAT to the net subtotal
  const vatAmount = applyVat ? round2(subtotal * vatPct) : 0;
  const grandTotal = round2(subtotal + vatAmount);

  return {
    materialsTotal,
    equipmentTotal,
    labourTotal,
    otherTotal,
    baseCost,
    adminFee,
    contingency,
    developer,
    preProfit,
    profit,
    grandTotal,
  };
}

/**
 * Get available statuses for a document based on its kind
 * - Invoices: draft, sent, paid, rejected
 * - Estimates & Quotes: draft, sent, accepted, cash, rejected
 */
export function getAvailableStatuses(kind: DocumentKind): DocumentStatus[] {
  const normalizedKind = String(kind || '').trim().toLowerCase();
  if (normalizedKind === 'invoice') {
    return ['draft', 'sent', 'paid', 'rejected'];
  } else {
    // Estimate, Quotation, CISCertificate
    return ['draft', 'sent', 'accepted', 'cash', 'rejected'];
  }
}

/**
 * Check if a status is valid for a given document kind
 */
export function isValidStatusForKind(status: DocumentStatus, kind: DocumentKind): boolean {
  return getAvailableStatuses(kind).includes(status);
}


